import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  bulkUpsertQuestions,
  deleteQuiz,
  getQuiz,
  updateQuiz,
  type OptionEditor,
  type QuestionEditor,
  type QuestionType,
  type QuizEditorDetail,
  type QuizShowAnswers,
} from "../api/quizzes";
import { ApiError } from "../api/client";
import { useCurrentUser } from "../hooks/useCurrentUser";

const QUESTION_TYPE_LABELS: Record<QuestionType, string> = {
  single_choice: "Один ответ",
  multi_choice: "Несколько ответов",
  true_false: "Истина / Ложь",
  text: "Текстовый ответ",
};

function emptyQuestion(): QuestionEditor {
  return {
    question_type: "single_choice",
    question_text: "",
    points: 1,
    explanation: "",
    correct_answer_text: "",
    options: [
      { option_text: "", is_correct: false },
      { option_text: "", is_correct: false },
    ],
  };
}

function canEdit(roles: string[]): boolean {
  return roles.some((r) => r === "super_admin" || r === "admin" || r === "content_manager");
}

export function QuizEditorPage() {
  const { quizId } = useParams<{ quizId: string }>();
  const id = Number(quizId);
  const { user } = useCurrentUser();
  const navigate = useNavigate();

  const [quiz, setQuiz] = useState<QuizEditorDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Editable settings
  const [title, setTitle] = useState("");
  const [passing, setPassing] = useState(80);
  const [maxAttempts, setMaxAttempts] = useState<string>("");
  const [shuffleQ, setShuffleQ] = useState(false);
  const [shuffleO, setShuffleO] = useState(false);
  const [timeLimit, setTimeLimit] = useState<string>("");
  const [showAfter, setShowAfter] = useState<QuizShowAnswers>("after_pass");
  const [description, setDescription] = useState("");

  // Questions state
  const [questions, setQuestions] = useState<QuestionEditor[]>([]);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!Number.isFinite(id)) {
      setError("Некорректный тест");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const q = await getQuiz(id);
      setQuiz(q);
      setTitle(q.title);
      setPassing(q.passing_score_percent);
      setMaxAttempts(q.max_attempts ? String(q.max_attempts) : "");
      setShuffleQ(q.shuffle_questions);
      setShuffleO(q.shuffle_options);
      setTimeLimit(q.time_limit_minutes ? String(q.time_limit_minutes) : "");
      setShowAfter(q.show_correct_answers_after);
      setDescription(q.description ?? "");
      setQuestions(q.questions);
      setDirty(false);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) setError("Тест не найден");
      else setError(err instanceof ApiError ? `Ошибка ${err.status}` : "Сеть недоступна");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!user) return null;
  const editable = canEdit(user.roles);

  function mark() {
    setDirty(true);
  }

  function updateQuestion(idx: number, patch: Partial<QuestionEditor>) {
    setQuestions((qs) => qs.map((q, i) => (i === idx ? { ...q, ...patch } : q)));
    mark();
  }

  function updateOption(qIdx: number, oIdx: number, patch: Partial<OptionEditor>) {
    setQuestions((qs) =>
      qs.map((q, i) =>
        i === qIdx
          ? {
              ...q,
              options: q.options.map((o, j) => (j === oIdx ? { ...o, ...patch } : o)),
            }
          : q,
      ),
    );
    mark();
  }

  function addQuestion() {
    setQuestions((qs) => [...qs, emptyQuestion()]);
    mark();
  }

  function removeQuestion(idx: number) {
    if (!confirm("Удалить вопрос?")) return;
    setQuestions((qs) => qs.filter((_, i) => i !== idx));
    mark();
  }

  function addOption(qIdx: number) {
    updateQuestion(qIdx, {
      options: [...questions[qIdx]!.options, { option_text: "", is_correct: false }],
    });
  }

  function removeOption(qIdx: number, oIdx: number) {
    updateQuestion(qIdx, {
      options: questions[qIdx]!.options.filter((_, j) => j !== oIdx),
    });
  }

  function setSingleCorrect(qIdx: number, oIdx: number) {
    updateQuestion(qIdx, {
      options: questions[qIdx]!.options.map((o, j) => ({ ...o, is_correct: j === oIdx })),
    });
  }

  async function handleSave() {
    if (!quiz) return;
    setSaving(true);
    try {
      // Save settings + bulk-upsert questions
      const settingsPatch: Parameters<typeof updateQuiz>[1] = {};
      if (title !== quiz.title) settingsPatch.title = title;
      if (description !== (quiz.description ?? "")) settingsPatch.description = description;
      if (passing !== quiz.passing_score_percent) settingsPatch.passing_score_percent = passing;
      const ma = maxAttempts ? Number(maxAttempts) : null;
      if (ma !== quiz.max_attempts) settingsPatch.max_attempts = ma;
      if (shuffleQ !== quiz.shuffle_questions) settingsPatch.shuffle_questions = shuffleQ;
      if (shuffleO !== quiz.shuffle_options) settingsPatch.shuffle_options = shuffleO;
      const tl = timeLimit ? Number(timeLimit) : null;
      if (tl !== quiz.time_limit_minutes) settingsPatch.time_limit_minutes = tl;
      if (showAfter !== quiz.show_correct_answers_after) {
        settingsPatch.show_correct_answers_after = showAfter;
      }
      if (Object.keys(settingsPatch).length > 0) {
        await updateQuiz(quiz.id, settingsPatch);
      }
      // Always save questions (cheap)
      await bulkUpsertQuestions(quiz.id, questions);
      await load();
    } catch (err) {
      alert(err instanceof ApiError ? `Ошибка ${err.status}` : "Не удалось");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!quiz) return;
    if (!confirm(`Удалить тест «${quiz.title}»?`)) return;
    try {
      await deleteQuiz(quiz.id);
      navigate("/quizzes");
    } catch (err) {
      alert(err instanceof ApiError ? `Ошибка ${err.status}` : "Не удалось");
    }
  }

  if (loading && !quiz) {
    return <div className="mx-auto max-w-4xl px-4 py-12 text-center text-slate-400">Загрузка…</div>;
  }

  if (error || !quiz) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-12 text-center">
        <p className="text-slate-500">{error ?? "Тест не найден"}</p>
        <Link to="/quizzes" className="mt-4 inline-block text-sm text-brand-accent hover:underline">
          ← к тестам
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl px-4 py-6">
      <header className="mb-6 flex flex-wrap items-start justify-between gap-2">
        <div>
          <Link to="/quizzes" className="text-sm text-slate-500 hover:text-brand-accent">
            ← к тестам
          </Link>
          <h1 className="mt-2 text-2xl font-semibold text-slate-900">{quiz.title}</h1>
        </div>
        {editable && (
          <div className="flex items-center gap-2">
            <span className={`text-xs ${dirty ? "text-amber-600" : "text-green-600"}`}>
              {dirty ? "не сохранено" : "✓ сохранено"}
            </span>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || !dirty}
              className="btn-primary"
            >
              {saving ? "Сохраняем…" : "Сохранить"}
            </button>
            <button
              type="button"
              onClick={handleDelete}
              className="btn-danger"
            >
              Удалить
            </button>
          </div>
        )}
      </header>

      {/* Settings */}
      <section className="mb-6 rounded-lg bg-white p-4 shadow-sm">
        <h2 className="mb-3 text-base font-semibold text-slate-900">Настройки</h2>
        <div className="grid gap-3 md:grid-cols-2">
          <div className="space-y-1 md:col-span-2">
            <label className="block text-sm font-medium text-slate-700">Название</label>
            <input
              type="text"
              value={title}
              disabled={!editable}
              onChange={(e) => {
                setTitle(e.target.value);
                mark();
              }}
              className="w-full rounded border border-slate-300 px-3 py-2 text-sm disabled:bg-slate-50"
            />
          </div>
          <div className="space-y-1 md:col-span-2">
            <label className="block text-sm font-medium text-slate-700">Описание</label>
            <textarea
              rows={2}
              value={description}
              disabled={!editable}
              onChange={(e) => {
                setDescription(e.target.value);
                mark();
              }}
              className="w-full rounded border border-slate-300 px-3 py-2 text-sm disabled:bg-slate-50"
            />
          </div>
          <div className="space-y-1">
            <label className="block text-sm font-medium text-slate-700">Проходной балл (%)</label>
            <input
              type="number"
              min={0}
              max={100}
              value={passing}
              disabled={!editable}
              onChange={(e) => {
                setPassing(Number(e.target.value));
                mark();
              }}
              className="w-full rounded border border-slate-300 px-3 py-2 text-sm disabled:bg-slate-50"
            />
          </div>
          <div className="space-y-1">
            <label className="block text-sm font-medium text-slate-700">
              Попыток (пусто = ∞)
            </label>
            <input
              type="number"
              min={1}
              value={maxAttempts}
              disabled={!editable}
              onChange={(e) => {
                setMaxAttempts(e.target.value);
                mark();
              }}
              className="w-full rounded border border-slate-300 px-3 py-2 text-sm disabled:bg-slate-50"
            />
          </div>
          <div className="space-y-1">
            <label className="block text-sm font-medium text-slate-700">
              Таймер (мин., опц.)
            </label>
            <input
              type="number"
              min={1}
              value={timeLimit}
              disabled={!editable}
              onChange={(e) => {
                setTimeLimit(e.target.value);
                mark();
              }}
              className="w-full rounded border border-slate-300 px-3 py-2 text-sm disabled:bg-slate-50"
            />
          </div>
          <div className="space-y-1">
            <label className="block text-sm font-medium text-slate-700">Показать ответы</label>
            <select
              value={showAfter}
              disabled={!editable}
              onChange={(e) => {
                setShowAfter(e.target.value as QuizShowAnswers);
                mark();
              }}
              className="w-full rounded border border-slate-300 px-3 py-2 text-sm disabled:bg-slate-50"
            >
              <option value="never">Никогда</option>
              <option value="after_pass">После сдачи</option>
              <option value="always">Всегда</option>
            </select>
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-700 md:col-span-1">
            <input
              type="checkbox"
              checked={shuffleQ}
              disabled={!editable}
              onChange={(e) => {
                setShuffleQ(e.target.checked);
                mark();
              }}
            />
            Перемешивать вопросы
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-700 md:col-span-1">
            <input
              type="checkbox"
              checked={shuffleO}
              disabled={!editable}
              onChange={(e) => {
                setShuffleO(e.target.checked);
                mark();
              }}
            />
            Перемешивать варианты
          </label>
        </div>
      </section>

      {/* Questions */}
      <section>
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-base font-semibold text-slate-900">
            Вопросы ({questions.length})
          </h2>
          {editable && (
            <button
              type="button"
              onClick={addQuestion}
              className="btn-secondary"
            >
              + Вопрос
            </button>
          )}
        </div>

        {questions.length === 0 ? (
          <div className="card border-dashed p-8 text-center text-sm text-ink-muted">
            Вопросов пока нет. Нажми «+ Вопрос» чтобы добавить.
          </div>
        ) : (
          <ol className="space-y-4">
            {questions.map((q, qIdx) => (
              <li key={qIdx} className="card p-5">
                {/* Question header */}
                <div className="mb-4 flex flex-wrap items-center justify-between gap-3 border-b border-line pb-3">
                  <div className="flex items-center gap-2">
                    <span className="grid h-7 w-7 place-items-center rounded-full bg-brand/10 text-xs font-semibold tabular-nums text-brand">
                      {qIdx + 1}
                    </span>
                    <span className="text-xs font-semibold uppercase tracking-wide text-ink-muted">
                      Вопрос
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <select
                      value={q.question_type}
                      disabled={!editable}
                      onChange={(e) => {
                        const newType = e.target.value as QuestionType;
                        if (newType === "true_false") {
                          updateQuestion(qIdx, {
                            question_type: newType,
                            options: [
                              { option_text: "Истина", is_correct: false },
                              { option_text: "Ложь", is_correct: false },
                            ],
                          });
                        } else {
                          updateQuestion(qIdx, { question_type: newType });
                        }
                      }}
                      className="input w-auto py-1 text-xs"
                    >
                      {Object.entries(QUESTION_TYPE_LABELS).map(([v, l]) => (
                        <option key={v} value={v}>
                          {l}
                        </option>
                      ))}
                    </select>
                    <label className="inline-flex items-center gap-1 text-xs text-ink-muted">
                      Баллы
                      <input
                        type="number"
                        min={1}
                        value={q.points}
                        disabled={!editable}
                        onChange={(e) =>
                          updateQuestion(qIdx, { points: Number(e.target.value) })
                        }
                        className="input w-16 py-1 text-xs"
                      />
                    </label>
                    {editable && (
                      <button
                        type="button"
                        onClick={() => removeQuestion(qIdx)}
                        className="text-xs font-medium text-brand-danger hover:underline"
                      >
                        Удалить
                      </button>
                    )}
                  </div>
                </div>

                {/* Question text */}
                <label className="block">
                  <span className="text-xs font-semibold uppercase tracking-wide text-ink-muted">
                    Текст вопроса
                  </span>
                  <textarea
                    rows={2}
                    required
                    placeholder="Например: Что символизирует топор в романе «Преступление и наказание»?"
                    value={q.question_text}
                    disabled={!editable}
                    onChange={(e) =>
                      updateQuestion(qIdx, { question_text: e.target.value })
                    }
                    className="input mt-1.5 w-full"
                  />
                </label>

                {/* Options or text-correct */}
                <div className="mt-4">
                  <div className="text-xs font-semibold uppercase tracking-wide text-ink-muted">
                    {q.question_type === "text"
                      ? "Правильный ответ"
                      : "Варианты ответа"}
                  </div>
                  {q.question_type === "text" ? (
                    <input
                      type="text"
                      placeholder="Точный ответ (case-insensitive, trim)"
                      value={q.correct_answer_text ?? ""}
                      disabled={!editable}
                      onChange={(e) =>
                        updateQuestion(qIdx, {
                          correct_answer_text: e.target.value,
                        })
                      }
                      className="input mt-1.5 w-full"
                    />
                  ) : (
                    <div className="mt-1.5 space-y-2">
                      {q.options.map((o, oIdx) => (
                        <div
                          key={oIdx}
                          className={`flex items-center gap-3 rounded-lg border-2 bg-white px-3 py-2 transition ${
                            o.is_correct
                              ? "border-brand-success bg-brand-success/5"
                              : "border-line"
                          }`}
                        >
                          {q.question_type === "multi_choice" ? (
                            <input
                              type="checkbox"
                              checked={o.is_correct}
                              disabled={!editable}
                              onChange={(e) =>
                                updateOption(qIdx, oIdx, {
                                  is_correct: e.target.checked,
                                })
                              }
                              className="h-4 w-4 cursor-pointer accent-brand-success"
                              aria-label="Правильный"
                            />
                          ) : (
                            <input
                              type="radio"
                              name={`q${qIdx}-correct`}
                              checked={o.is_correct}
                              disabled={!editable}
                              onChange={() => setSingleCorrect(qIdx, oIdx)}
                              className="h-4 w-4 cursor-pointer accent-brand-success"
                              aria-label="Правильный"
                            />
                          )}
                          <input
                            type="text"
                            required
                            placeholder="Вариант ответа"
                            value={o.option_text}
                            disabled={
                              !editable || q.question_type === "true_false"
                            }
                            onChange={(e) =>
                              updateOption(qIdx, oIdx, {
                                option_text: e.target.value,
                              })
                            }
                            className="flex-1 border-0 bg-transparent text-sm focus:outline-none focus:ring-0 disabled:text-ink"
                          />
                          {o.is_correct && (
                            <span className="badge badge-success shrink-0">
                              Правильный
                            </span>
                          )}
                          {editable && q.question_type !== "true_false" && (
                            <button
                              type="button"
                              onClick={() => removeOption(qIdx, oIdx)}
                              aria-label="Удалить вариант"
                              className="shrink-0 text-ink-muted transition hover:text-brand-danger"
                            >
                              ×
                            </button>
                          )}
                        </div>
                      ))}
                      {editable && q.question_type !== "true_false" && (
                        <button
                          type="button"
                          onClick={() => addOption(qIdx)}
                          className="text-xs font-medium text-brand hover:underline"
                        >
                          + Добавить вариант
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {/* Explanation */}
                <label className="mt-4 block">
                  <span className="text-xs font-semibold uppercase tracking-wide text-ink-muted">
                    Пояснение (показывается после ответа)
                  </span>
                  <textarea
                    rows={2}
                    placeholder="Например: топор — это не просто орудие, а символ внутреннего раскола героя."
                    value={q.explanation ?? ""}
                    disabled={!editable}
                    onChange={(e) =>
                      updateQuestion(qIdx, { explanation: e.target.value })
                    }
                    className="input mt-1.5 w-full text-xs"
                  />
                </label>
              </li>
            ))}
          </ol>
        )}
      </section>

      {editable && questions.length > 0 && (
        <div className="mt-6 flex justify-end">
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || !dirty}
            className="rounded bg-brand-accent px-6 py-2 text-sm font-medium text-white transition hover:bg-brand-dark disabled:bg-slate-400"
          >
            {saving ? "Сохраняем…" : dirty ? "Сохранить изменения" : "✓ Сохранено"}
          </button>
        </div>
      )}
    </div>
  );
}
